import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { Combobox } from '@/components/ui/combobox';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
} from '@/components/ui/table';
import { useOffcuts } from '@/hooks/offcuts/use-offcuts';
import { OffcutRow } from './components/offcut-row';
import type { OffcutItemDto } from '@cutlyft/zod-schemas';

type StatusFilter = OffcutItemDto['inventoryStatus'] | 'ALL';

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All statuses' },
  { value: 'AVAILABLE', label: 'Available' },
  { value: 'CONSUMED', label: 'Consumed' },
  { value: 'SCRAPPED', label: 'Scrapped' },
];

export default function OffcutsPage() {
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('ALL');
  const [cursor, setCursor] = React.useState<number | undefined>();

  const { data, isLoading } = useOffcuts({
    inventoryStatus: statusFilter === 'ALL' ? undefined : statusFilter,
    cursor,
    limit: 50,
  });

  const offcuts = data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-fg-primary">Off-cuts</h1>
        <p className="text-fg-secondary mt-1">View and manage reusable offcut inventory.</p>
      </div>

      <div className="flex items-center gap-3">
        <div className="w-44">
          <Combobox
            options={STATUS_OPTIONS}
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter((v ?? 'ALL') as StatusFilter);
              setCursor(undefined);
            }}
            placeholder="All statuses"
            searchPlaceholder="Search status..."
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-5 w-5 animate-spin text-fg-muted" />
        </div>
      ) : offcuts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex h-32 items-center justify-center text-fg-muted text-sm">
            No offcuts found.
          </CardContent>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">ID</TableHead>
              <TableHead>Length</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Source</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {offcuts.map((offcut) => (
              <OffcutRow key={offcut.id} offcut={offcut} />
            ))}
          </TableBody>
        </Table>
      )}

      {data?.hasMore && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCursor(data.nextCursor ?? undefined)}
        >
          Load more
        </Button>
      )}
    </div>
  );
}
